import { useCallback, useMemo } from "react";

import type { SiteControllerSettingBaseFormModel } from "@/components/features/settings/site-controller/model/base/type";
import type { SiteControllerSettingBeds24FormModel } from "@/components/features/settings/site-controller/model/beds24/type";
import type { SiteControllerSettingBeds24FormModelMethodsType } from "@/components/features/settings/site-controller/model/beds24/use-case/use-create-model/methods";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type { FormOptionType } from "@/components/ui/form/_types/type";

type Props = {
	beds24RoomTypeMapping: SiteControllerSettingBeds24FormModel["ViewModel"]["beds24RoomTypeMapping"]; //
	onSetBeds24RoomTypeMapping: SiteControllerSettingBeds24FormModelMethodsType["siteControllerSettingBeds24FormModelMethods"]["onSetBeds24RoomTypeMapping"];

	onSyncMasterData: SiteControllerSettingBeds24FormModelMethodsType["siteControllerSettingBeds24FormModelMethods"]["onSyncMasterData"];
	canSyncMasterData: SiteControllerSettingBeds24FormModelMethodsType["siteControllerSettingBeds24FormModelMethods"]["canSyncMasterData"];

	roomKVList: SiteControllerSettingBaseFormModel["QueryData"]["roomKVList"];
};

const NULL = "null";

export const useBeds24RoomTypeMapping = ({
	beds24RoomTypeMapping,
	onSetBeds24RoomTypeMapping: _onSetBeds24RoomTypeMapping,
	roomKVList,
	onSyncMasterData: _onSyncMasterData,
	canSyncMasterData,
}: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const onSyncMasterData = useCallback(async () => {
		showLoadingScreenCallback();

		await _onSyncMasterData().finally(() => {
			hideLoadingScreenCallback();
		});
	}, [_onSyncMasterData, hideLoadingScreenCallback, showLoadingScreenCallback]);

	const selectedChillnnRoomIDs = useMemo(
		() =>
			beds24RoomTypeMapping.value
				.filter((item) => Boolean(item.roomID?.value))
				.map((roomType) => roomType.roomID!.value!),
		[beds24RoomTypeMapping.value],
	);

	const roomTypeMappingOptions: (selectedRoomID: string | undefined) => FormOptionType[] = (selectedRoomID) => {
		return [
			{ id: "roomTypeMappingOptions_0", label: "紐付けしない", value: NULL },
			...roomKVList
				.filter((item) => selectedRoomID === item.roomID || !selectedChillnnRoomIDs.includes(item.roomID))
				.map((room, idx) => {
					return {
						label: room.name,
						value: room.roomID,
						id: room.name + room.roomID + idx,
					} satisfies FormOptionType;
				}),
		] satisfies FormOptionType[];
	};

	const onSetBeds24RoomTypeMapping = useCallback(
		(value: { roomID: string; beds24RoomID: string }) => {
			if (value.roomID === NULL) {
				value.roomID = null as unknown as string;
			}

			_onSetBeds24RoomTypeMapping(value);
		},
		[_onSetBeds24RoomTypeMapping],
	);

	return {
		roomTypeMappingOptions, //
		onSetBeds24RoomTypeMapping,

		canSyncMasterData,
		onSyncMasterData,
	};
};
